'use client';

import { useState } from 'react';
import { Star, ThumbsUp } from 'lucide-react';

interface Review {
  id: number;
  author: string;
  rating: number;
  title: string;
  content: string;
  date: string;
  helpful: number;
}

interface ReviewItemProps {
  review: Review;
  onMarkHelpful?: (reviewId: number) => void;
}

export function ReviewItem({ review, onMarkHelpful }: ReviewItemProps) {
  const [isHelpful, setIsHelpful] = useState(false);

  const handleHelpful = () => {
    if (!isHelpful) {
      setIsHelpful(true);
      onMarkHelpful?.(review.id);
    }
  };

  return (
    <div className="group rounded-lg border border-slate-700 bg-slate-800/30 p-6 backdrop-blur transition-all duration-300 hover:border-slate-600 hover:bg-slate-800/50">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-white mb-1">{review.title}</h3>
          <p className="text-sm text-slate-400">
            by <span className="text-slate-300">{review.author}</span> • {review.date}
          </p>
        </div>
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={16}
              className={`transition-all duration-300 ${
                i < review.rating
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-slate-600'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <p className="mb-4 text-slate-300 leading-relaxed">{review.content}</p>

      {/* Footer */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleHelpful}
          disabled={isHelpful}
          className={`inline-flex items-center gap-2 rounded px-3 py-2 text-sm font-medium transition-all duration-300 ${
            isHelpful
              ? 'bg-amber-500/20 text-amber-300'
              : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
          } disabled:cursor-not-allowed`}
        >
          <ThumbsUp
            size={16}
            className={`transition-transform duration-300 ${
              isHelpful ? 'fill-amber-300' : ''
            }`}
          />
          Helpful ({review.helpful})
        </button>
      </div>
    </div>
  );
}
